<template>
  <transition name="slide">
    <div
      v-if="node && node.selected"
      class="cf-node-properties cf-node-properties--enhanced-form"
      @contextmenu.stop.prevent=""
    >
      <div class="cf-node-properties__header">
        <div class="cf-node-properties__title">
          {{ node.displayName }}
          <i 
            v-tooltip.top="node.nodeDescription"
            class="fa fa-question-circle m-l-8"
            aria-hidden="true"
          />
        </div>
        <div class="cf-node__actions">
          <button 
            v-tooltip.left.hover="'Duplicate Node'"
            class="cf-node__action"
            @click.stop="duplicateNode($event, true)"
          >
            <i
              class="fa fa-copy"
              aria-hidden="true"
            />
          </button>

          <button 
            v-tooltip.left.hover="'Delete Node'"
            class="cf-node__action m-l-15"
            @click.stop="deleteNode"
          >
            <i
              class="fa fa-trash"
              aria-hidden="true"
            />
          </button>

          <button 
            v-tooltip.left.hover="'Close Settings'"
            class="cf-node__action m-l-15"
            @click.stop="closeNodeProperties"
          >
            <i
              class="fa fa-times"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <div class="cf-node-properties__content">
        <div
          v-if="hasErrors"
          class="cf-node-properties__errors"
        >
          <ul>
            <template v-if="generalErrors">
              <li
                v-for="(error, index) in generalErrors"
                :key="`'gen-error-'${index}`"
              >
                {{ error }}
              </li>
            </template>

            <template v-if="propertyErrors">
              <li
                v-for="(error, index) in propertyErrors"
                :key="`'prop-error-${index}`"
              >
                {{ error.displayName }} {{ error.errorMessage }}.
              </li>
            </template>
          </ul>
        </div>


        <div class="cf-node-properties__field">
          <label class="control-label">
            Node Name
            <i
              v-tooltip.top="'A label that will be populated on top of the node with the selected name'"
              class="fa fa-question-circle m-l-5"
              aria-hidden="true"
            />
          </label>
          

          <InputText
            v-model="stepName"
            @input="onUpdateStepNameDelayed"
            @blur="onUpdateStepName"
          />
        </div>

        <DialNodeProps
          v-if="node.nodeTypeId == 'DialNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <RouterNodeProps
          v-else-if="node.nodeTypeId == 'RouterNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
          :scene="scene"
          :routes.sync="routes"
          @showContextMenu="showContextMenu"
        />
        <MenuNodeProps
          v-else-if="node.nodeTypeId == 'MenuNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
          :routes.sync="routes"
          @removeConnection="removeConnection"
          @showContextMenu="showContextMenu"
        />
        <SplitTestNodeProps
          v-else-if="node.nodeTypeId == 'SplitTestNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
          :routes.sync="routes"
          @removeConnection="removeConnection"
          @showContextMenu="showContextMenu"
        />
        <PlayNodeProps
          v-else-if="node.nodeTypeId == 'PlayNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <GatherNodeProps
          v-else-if="node.nodeTypeId == 'GatherNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <HangupNodeProps
          v-else-if="node.nodeTypeId == 'HangupNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <JsNodeProps
          v-else-if="node.nodeTypeId == 'JSNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <GoToNodeProps
          v-else-if="node.nodeTypeId == 'GoToNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
          :nodes="scene.nodes"
          :selected-node-to-route="selectedNodeToRoute"
          @goToNodeSelectMode="goToNodeSelectMode"
          @selectNode="selectNode"
        />
        <HoursFilterNodeProps
          v-else-if="node.nodeTypeId == 'HoursOfOperationNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <RecordNodeProps
          v-else-if="node.nodeTypeId == 'RecordNode'"
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <GenericNodeProps
          v-else
          :key="`node-${node.id}`"
          :node.sync="nodeClone"
        />
        <div class="cf-node-properties__action-buttons">
          <!-- Start comment, removing Update button -->
          <!-- <PrimaryButton
            size="sm"
            @click="updateNodeSettings"
          >
            <span v-t="'UPDATE'" />
          </PrimaryButton> -->
          <!-- End comment -->

          <button 
            class="btn btn-sm btn-danger m-l-15"
            @click="closeNodeProperties"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { cloneDeep } from 'lodash';
import messages from '../../../locales';
import sharedMessages from '@/core/locales';
import InputText from './Form/InputText';
import DialNodeProps from './DialNode';
import RouterNodeProps from './RouterNode';
import GoToNodeProps from './GoToNode';
import MenuNodeProps from './MenuNode';
import SplitTestNodeProps from './SplitTestNode';
import GatherNodeProps from './GatherNode';
import HangupNodeProps from './HangupNode';
import HoursFilterNodeProps from './HoursFilterNode';
import GenericNodeProps from './GenericNode';
import JsNodeProps from './JsNode';
import RecordNodeProps from './RecordNode';
import PlayNodeProps from './PlayNode';
import { mapActions, mapState } from 'vuex';
import nodePropsMixin from '@/modules/ivrs/mixins/nodePropsMixin';
import debounce from 'lodash/debounce';
import { getNodeStepName } from '@/modules/ivrs/helpers/node';

export default {
  name: 'NodeProperties',
  components: { 
    InputText,
    DialNodeProps, 
    RouterNodeProps,
    GoToNodeProps,
    MenuNodeProps,
    HangupNodeProps,
    HoursFilterNodeProps,
    GatherNodeProps,
    GenericNodeProps,
    JsNodeProps,
    RecordNodeProps,
    PlayNodeProps,
    SplitTestNodeProps
  },

  mixins: [nodePropsMixin],

  props: {
    scene: {
      type: Object,
      default: null
    },
    selectModeActive: {
      type: Boolean,
      default: false,
    },
    routeToNode: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      nodeLocalCopy: null,
      stepName: '',
      selectedNodeToRoute: null,
    };
  },
  computed: {
    ...mapState('callFlows', {
      links: state => state.scene.links,
    }),

    routes: {
      get() {
        return this.links?.filter((link) => link.originNode.id == this.node.id) || [];
      },

      set(value) {
        this.updateLinks({routes: value, reorderRoutes: this.node.nodeTypeId == 'RouterNode'});
        this.setNodeErrors(this.nodeClone);
      }
    },

    nodeClone: {
      get() {
        return this.nodeLocalCopy;
      },

      set(value) {
        this.updateNode(value);
      }
    },
  },
  watch: {
    'node': {
      handler(newValue) {
        this.nodeLocalCopy = cloneDeep(newValue);
        this.stepName = getNodeStepName(this.nodeLocalCopy);
      },
      immediate: true,
      deep: true
    },

    'selectModeActive': {
      handler: function() {
        if (!this.selectModeActive && this.routeToNode) {
          this.selectedNodeToRoute = this.routeToNode;
        } else {
          this.selectedNodeToRoute = null;
        }
      }
    }
  },
  methods: {
    ...mapActions('callFlows', [
      'updateLinks',
      'setNodeErrors',
    ]),

    ...mapActions('callFlows', {
      updateNode: 'updateNodeSettings',
      updateNodeStepName : 'updateNodeStepName'
    }),

    // Start comment - Remove update node via API mechanism for now
    // updateNodeSettings() {
    //   this.$emit('updateNodeSettings', this.nodeClone);
    // },
    // End comment
    duplicateNode() {
      const dupeNode = cloneDeep(this.nodeClone);
      dupeNode.id = '';
      this.$emit('duplicateNode', dupeNode);
    },
    deleteNode() {
      this.$emit('deleteNode', this.node);
    },
    closeNodeProperties() {
      this.$emit('closeNodeProperties', this.nodeClone);
    },
    goToNodeSelectMode() {
      this.$emit('goToNodeSelectMode', true);
    },

    onUpdateStepNameDelayed: debounce(function onUpdateStepNameDelayed() { this.onUpdateStepName(); }, 600),

    onUpdateStepName() {
      this.updateNodeStepName({ id: this.nodeLocalCopy.id, stepName: this.stepName });
    },

    selectNode(node) {
      this.$emit('selectNode', node);
    },
    removeConnection(event) {
      this.$emit('removeConnection', event);
    },
    showContextMenu(event, args) {
      this.$emit('showContextMenu', event, args);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>